import { useEffect, useState, useRef } from 'react';

/**
 * Animate a numeric value from its previous render to the new target.
 * Uses requestAnimationFrame for smoothness; respects `prefers-reduced-motion`
 * by jumping straight to the target for users who opt out of motion.
 */
export const useAnimatedCounter = (target, durationMs = 700) => {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || target === fromRef.current) {
      setValue(target);
      fromRef.current = target;
      return undefined;
    }

    const start = performance.now();
    const from = fromRef.current;
    const delta = target - from;

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + delta * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
};

export default useAnimatedCounter;
